import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  DataGridPro,
} from '@mui/x-data-grid-pro';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

import WorkflowDrawer from './WorkflowDrawer';
import UnClassifiedWorkflowDrawer from './UnClassifiedWorkflowDrawer';
import PDFViewer from './PDFViewer';
import UnClassifiedPDFViewer from './UnClassifiedPDFViewer';
import FileUploadDialog from './FileUploadDialog';
import ReferenceDrawer from './ReferenceDrawer';
import ReferenceFileList from './ReferenceFileList'; // Import the ReferenceFileList component
import OutputViewer from './OutputViewer'; // Import the OutputViewer component
import {
  filterWorkflowsByProductScope,
  workflowProductFunctionCode,
  workflowProductTitle,
} from './workflowProductScope';

// Import the common toast components
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';

// Import user rights context
import { useUserRights } from '../../../context/UserRightsContext';
import { FC } from '../../../constants/functionCodes';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonatePlainIconButtonSx,
  harmonateToolbarButtonSx,
} from '../../../theme/harmonateStyles';

// Utility function to get tenant_id from /conductor/get-current-user
const getTenantId = async () => {
  // const token = localStorage.getItem('token');
  // if (!token) return null;

  // const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/get-current-user`, {
  //   headers: {
  //     'Authorization': `Bearer ${token}`,
  //     'Content-Type': 'application/json'
  //   }
  // });

  // if (response.ok) {
  //   const data = await response.json();
  //   return data.tenant_id;
  // }
  // return null;
  return localStorage.getItem('tenant_id')
};

// Helper to convert quarter label (e.g., "2026 Q1") to date range
const parseQuarterLabel = (label) => {
  // Parse format: "YYYY QN" (e.g., "2026 Q1")
  const match = label.match(/(\d{4})\s+Q(\d)/);
  if (!match) {
    throw new Error(`Invalid quarter label format: ${label}`);
  }
  
  const year = parseInt(match[1], 10);
  const quarter = parseInt(match[2], 10);
  
  // Calculate date ranges for each quarter
  let fromMonth, fromDay, toMonth, toDay;
  
  switch (quarter) {
    case 1: // Q1: Jan 1 - Mar 31
      fromMonth = 0; fromDay = 1;
      toMonth = 2; toDay = 31;
      break;
    case 2: // Q2: Apr 1 - Jun 30
      fromMonth = 3; fromDay = 1;
      toMonth = 5; toDay = 30;
      break;
    case 3: // Q3: Jul 1 - Sep 30
      fromMonth = 6; fromDay = 1;
      toMonth = 8; toDay = 30;
      break;
    case 4: // Q4: Oct 1 - Dec 31
      fromMonth = 9; fromDay = 1;
      toMonth = 11; toDay = 31;
      break;
    default:
      throw new Error(`Invalid quarter number: ${quarter}`);
  }
  
  const fromDate = new Date(year, fromMonth, fromDay);
  const toDate = new Date(year, toMonth, toDay);
  
  // Format as YYYY-MM-DD
  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  return {
    label,
    from: formatDate(fromDate),
    to: formatDate(toDate),
    year,
    quarter_number: quarter
  };
};

// Helper to get quarter date ranges from API
const fetchQuarterRanges = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Get tenant_integration from localStorage
    const tenantIntegration = localStorage.getItem('tenant_integration');
    if (!tenantIntegration) {
      throw new Error('No tenant integration found in localStorage');
    }

    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/dropdown-data/quarter-dropdown-values?tenant_id=${encodeURIComponent(tenantIntegration)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
   
    if (!response.ok) {
      throw new Error(`Failed to fetch quarters: ${response.status}`);
    }
    
    const quarterLabels = await response.json();
    
    // Validate response is an array
    if (!Array.isArray(quarterLabels)) {
      throw new Error('Invalid response format: expected array of quarter labels');
    }
   
    // Transform quarter labels to quarter objects with date ranges
    return quarterLabels.map(label => parseQuarterLabel(label));
  } catch (error) {
    console.error('Error fetching quarters from API:', error);
    // Re-throw the error so the calling function can handle it properly
    throw error;
  }
};


// Helper to get current quarter index
const getCurrentQuarterIndex = (quarters, date = new Date()) => {
  // Use YYYY-MM-DD format for local date comparison
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const currentDate = `${year}-${month}-${day}`;
  
  const currentQuarterIndex = quarters.findIndex(quarter => 
    currentDate >= quarter.from && currentDate <= quarter.to
  );
  return currentQuarterIndex !== -1 ? currentQuarterIndex : 0;
};

const Workflows = ({ productScope = null }) => {
  const [quarterAnchorEl, setQuarterAnchorEl] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null); // New state for 3-dot menu
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [currentView, setCurrentView] = useState('workflows');
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedWorkflowInstanceId, setSelectedWorkflowInstanceId] = useState(null);
  const [selectedFastApiFileInstanceId, setSelectedFastApiFileInstanceId] = useState(null);
  const [selectedReferenceData, setSelectedReferenceData] = useState(null);
  const [isUnclassifiedView, setIsUnclassifiedView] = useState(false);
  const [isViewingUnclassifiedFile, setIsViewingUnclassifiedFile] = useState(false);
  const [isOutputView, setIsOutputView] = useState(false);
  const [workflowData, setWorkflowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add state for reference navigation

  // Add state for quarters and selected quarter
  const [quarterRanges, setQuarterRanges] = useState([]);
  const [quartersLoading, setQuartersLoading] = useState(true);
  const [selectedQuarterIdx, setSelectedQuarterIdx] = useState(0);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  // Set default category to 'WF' for initial load
  const [selectedCategory, setSelectedCategory] = useState('WF');
  const [categoryAnchorEl, setCategoryAnchorEl] = useState(null);

  // Use the common toast hook
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();

  // Use user rights context
  const { hasPermission, isHarmonateUser } = useUserRights();
  const scopedFunctionCode = workflowProductFunctionCode(productScope);
  const canUploadWorkflow = isHarmonateUser
    || (scopedFunctionCode
      ? hasPermission(scopedFunctionCode, 'upload')
      : (hasPermission(FC.DOCUMENT_VALIDATION, 'upload') || hasPermission(FC.ETVL_ENGINE, 'upload')));
  const canViewWorkflow = isHarmonateUser
    || (scopedFunctionCode
      ? hasPermission(scopedFunctionCode, 'view')
      : (hasPermission(FC.DOCUMENT_VALIDATION, 'view') || hasPermission(FC.ETVL_ENGINE, 'view')));

  const visibleWorkflowData = useMemo(
    () => filterWorkflowsByProductScope(workflowData, productScope),
    [workflowData, productScope]
  );
  const pageTitle = workflowProductTitle(productScope);

  // Load quarters on component mount
  useEffect(() => {
    const loadQuarters = async () => {
      try {
        setQuartersLoading(true);
        const quarters = await fetchQuarterRanges();
        
        // Display all quarters from API response - no filtering
        setQuarterRanges(quarters);
        
        // Set "All Quarters" as default
        setSelectedQuarterIdx(-1);
        setSelectedQuarter('all');
        
        showSuccess(`Loaded ${quarters.length} quarters`, 'Quarters Loaded');
      } catch (error) {
        console.error('Error loading quarters:', error);
        showError('Failed to load quarters', 'Quarter Loading Error');
      } finally {
        setQuartersLoading(false);
      }
    };

    loadQuarters();
  }, []);

  // Fetch workflow data from API
  const fetchWorkflowData = async (quarter = selectedQuarter) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      // Get tenant_id from /conductor/get-current-user
      const tenantId = await getTenantId();
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }
      // Call the new API for workflow data with quarter and category filtering
      // If quarter is null/undefined (All Quarters), don't include from/to parameters
      let url = `${process.env.REACT_APP_BACKEND_URL}conductor/docs/workflow-stats/${tenantId}?`;
      if (quarter && quarter !== 'all') {
        url += `&from=${encodeURIComponent(quarter.from)}&to=${encodeURIComponent(quarter.to)}`;
      }
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) {
        throw new Error(`Failed to fetch workflow data: ${resp.status} ${resp.statusText}`);
      }
      const data = await resp.json();
      
      // Transform the workflow stats
      const transformedData = [];
      
      // Add regular workflows
      if (Array.isArray(data.workflowStats)) {
        data.workflowStats.forEach(item => {
          transformedData.push({
            id: item.workflowId,
            documentType: item.workflowName,
            inProcess: item.inProcessCount || 0,
            exceptions: item.exceptionsCount || 0,
            complete: item.completedCount || 0,
            failed: item.failedCount || 0,
            workflowId: item.workflowId,
            total: item.count || 0,
            documentTypeId: item.workflowId,
            isUnidentified: false
          });
        });
      }
      
      // Add unidentified documents as a special entry if there are any
      if (data.unidentifiedCount > 0) {
        transformedData.push({
          id: 'unidentified',
          documentType: 'Unidentified Documents',
          inProcess: data.unidentifiedInProcessCount || 0,
          exceptions: data.unidentifiedExceptionsCount || 0,
          complete: data.unidentifiedCompletedCount || 0,
          failed: data.unidentifiedFailedCount || 0,
          workflowId: 'unidentified',
          total: data.unidentifiedCount || 0,
          documentTypeId: 'unidentified',
          isUnidentified: true
        });
      }
      setWorkflowData(transformedData);
      const visibleCount = filterWorkflowsByProductScope(transformedData, productScope).length;
      if (visibleCount === 0) {
        showInfo('No workflows found', 'Empty Results');
      } else {
        showSuccess(`Successfully loaded ${visibleCount} workflows`, 'Data Loaded');
      }
    } catch (err) {
      console.error('Error fetching workflow data:', err);
      setError(err.message);
      setWorkflowData([]);
      showError(`Failed to load workflows: ${err.message}`, 'Loading Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedQuarter !== null) {
      fetchWorkflowData(selectedQuarter);
    }
    // eslint-disable-next-line
  }, [selectedQuarter]);

  const alerts = [
    { investorName: 'Opus Elevated, LP', errorDetails: 'Extracted Accounted Income Allocation Current Period Change does not match reference data', metadata: 'Distributions mismatch' },
    { investorName: 'Cadence Line, LP', errorDetails: 'Distributions do not match reference data', metadata: 'Value discrepancy' },
  ];

  const handleQuarterClick = (event) => setQuarterAnchorEl(event.currentTarget);
  const handleQuarterClose = () => setQuarterAnchorEl(null);

  // New handlers for 3-dot menu
  const handleMenuClick = (event) => setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleOpenUploadDialog = () => {
    // Check if user has upload permission for Workflow function
    if (!canUploadWorkflow) {
      showError('You do not have permission to upload files', 'Access Denied');
      return;
    }
    setOpenUploadDialog(true);
  };
  const handleCloseUploadDialog = () => setOpenUploadDialog(false);

  const handleRowClick = (params) => {
    // Check if user has view permission for Workflow function
    if (!canViewWorkflow) {
      showError('You do not have permission to view workflow details', 'Access Denied');
      return;
    }

    const { documentType, documentTypeId, isUnidentified } = params.row;
    setSelectedDocumentType(documentType);
    setSelectedWorkflowId(documentTypeId);
    
    // Handle Unidentified Documents
    if (isUnidentified) {
      setIsUnclassifiedView(true);
      setSelectedWorkflowId('unidentified');
      showInfo('Viewing unidentified documents');
    } else {
      // Handle regular workflow documents
      setIsUnclassifiedView(false);
      showInfo(`Opening ${documentType} workflow details`);
    }
    
    setCurrentView('workflowDetails');
  };

  const handleViewUnclassified = () => {
    // Check if user has view permission for Workflow function
    if (!canViewWorkflow) {
      showError('You do not have permission to view workflow details', 'Access Denied');
      return;
    }

    setSelectedDocumentType('Unidentified');
    setSelectedWorkflowId('unclassified');
    setIsUnclassifiedView(true);
    setCurrentView('workflowDetails');
    showInfo('Viewing unidentified documents');
  };

  // New handler for Reference Files option
  const handleReferenceFiles = () => {
    handleMenuClose();
    if (!canViewWorkflow) {
      showError('You do not have permission to view workflow details', 'Access Denied');
      return;
    }
    setCurrentView('referenceDrawer');
    setSelectedReferenceData(null); // Reset reference data when going to drawer
    showInfo('Opening Reference Files', 'Navigation');
  };

  // New handler for navigating to reference file list
  const handleNavigateToReferenceFiles = (referenceData, quarter) => {
    if (!canViewWorkflow) {
      showError('You do not have permission to view workflow details', 'Access Denied');
      return;
    }
    setSelectedReferenceData(referenceData);
    // If quarter is provided from ReferenceDrawer, use it; otherwise use the current selectedQuarter
    if (quarter) {
      setSelectedQuarter(quarter);
    }
    setCurrentView('referenceFiles');
    showInfo(`Opening ${referenceData.documentType} files`, 'Navigation');
  };

  // New handler for going back to reference drawer
  const handleBackToReferenceDrawer = () => {
    setCurrentView('referenceDrawer');
    setSelectedReferenceData(null);
    showInfo('Returned to reference files', 'Navigation');
  };

  const handleFileClick = async (fileData, isUnclassified = false) => {
    setIsViewingUnclassifiedFile(isUnclassified);
    
    // For unclassified files, we don't need to fetch workflow instance
    if (!isUnclassified) {
      let workflowInstanceId = null;
      if (fileData.unitOfWorkId) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/workflow-executions/unit-of-work/${fileData.unitOfWorkId}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (response.ok) {
            workflowInstanceId = await response.json();
          }
        } catch (err) {
          console.error('Error fetching workflow instance:', err);
        }
      }
      setSelectedWorkflowInstanceId(workflowInstanceId);
    }
    
    // Log document view
    try {
      const { AuditLoggers } = await import('../../../utils/auditLogger');
      await AuditLoggers.documentViewed(fileData.fileInstanceId, fileData.fileName);
    } catch (error) {
      console.error('Error logging document view:', error);
    }
    
    setSelectedFile({
      fileInstanceId: fileData.fileInstanceId,
      fileName: fileData.fileName,
      id: fileData.id,
      unitOfWorkId: fileData.unitOfWorkId,
      approvedStatus: fileData.approvedStatus,
      workflowDefinitionSnowflakeId: fileData.workflowDefinitionSnowflakeId
    });
    
    console.log('Setting selectedFile with data:', {
      fileInstanceId: fileData.fileInstanceId,
      fileName: fileData.fileName,
      id: fileData.id,
      unitOfWorkId: fileData.unitOfWorkId,
      approvedStatus: fileData.approvedStatus
    });
    setSelectedFastApiFileInstanceId(fileData.fastApiFileInstanceId);
    setCurrentView('pdfViewer');
    showInfo(`Opening file: ${fileData.fileName}`);
  };

  const handleBackToWorkflows = () => {
    setCurrentView('workflows');
    setSelectedFile(null);
    setSelectedDocumentType(null);
    setSelectedWorkflowId(null);
    setSelectedReferenceData(null); // Reset reference data
    setIsUnclassifiedView(false);
    setIsOutputView(false);
    showInfo('Returned to workflows view');
  };

  const handleUploadComplete = (results) => {
    console.log('Upload completed:', results);
    showSuccess('File upload completed successfully', 'Upload Complete');
    // Refresh workflow data after upload
    fetchWorkflowData();
    setTimeout(() => {
      handleCloseUploadDialog();
    }, 2000);
  };

  const handleRetry = () => {
    fetchWorkflowData();
  };

  const handleQuarterSelect = (quarterLabel) => {
    handleQuarterClose();
    if (quarterLabel === 'All Quarters') {
      setSelectedQuarterIdx(-1);
      setSelectedQuarter('all');
      showInfo('Showing all quarters', 'Quarter Changed');
    } else {
      const idx = quarterRanges.findIndex(q => q.label === quarterLabel);
      if (idx !== -1) {
        setSelectedQuarterIdx(idx);
        setSelectedQuarter(quarterRanges[idx]);
        showInfo(`Switched to ${quarterLabel}`, 'Quarter Changed');
      }
    }
  };

  // Handler for Output option
  const handleOutputView = () => {
    handleMenuClose();
    if (!canViewWorkflow) {
      showError('You do not have permission to view workflow details', 'Access Denied');
      return;
    }
    setCurrentView('outputViewer');
    setIsOutputView(true);
    showInfo('Opening Output Viewer', 'Navigation');
  };

  if (loading) {
    return (
      <Box sx={{ ...harmonatePageSx, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.white }} />
        <Typography variant="body2" sx={{ ml: 2, color: ht.white }}>Loading workflows...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...harmonatePageSx, p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: '500px' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load workflow data
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />}
          onClick={handleRetry} 
          sx={harmonateProceedButtonSx}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const isListView = currentView === 'workflows';

  return (
    <Box
      sx={{
        ...harmonatePageSx,
        p: isListView ? 3 : 0,
        overflowY: isListView ? 'auto' : 'hidden',
        overflowX: 'hidden',
      }}
    >
      {currentView === 'workflows' ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h1" sx={harmonatePageTitleSx}>
              {pageTitle}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Tooltip
                title={!canUploadWorkflow ? 'You do not have permission to upload files' : ''}
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={handleOpenUploadDialog}
                    disabled={!canUploadWorkflow}
                    sx={{
                      ...harmonateProceedButtonSx,
                      ...((!canUploadWorkflow)
                        ? { bgcolor: ht.fadedText, '&:hover': { bgcolor: ht.fadedText } }
                        : {}),
                    }}
                  >
                    File Upload
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handleQuarterClick}
                disabled={quartersLoading}
                sx={harmonateToolbarButtonSx}
              >
                {quartersLoading ? 'Loading...' : selectedQuarter === 'all' ? 'All Quarters' : selectedQuarter?.label || 'Select Quarter'}
              </Button>
              <Menu anchorEl={quarterAnchorEl} open={Boolean(quarterAnchorEl)} onClose={handleQuarterClose}>
                <MenuItem onClick={() => handleQuarterSelect('All Quarters')}>All Quarters</MenuItem>
                {quarterRanges.map((q, idx) => (
                  <MenuItem key={q.label} onClick={() => handleQuarterSelect(q.label)}>{q.label}</MenuItem>
                ))}
              </Menu>
              
              {/* 3-dot menu button */}
              <IconButton
                onClick={handleMenuClick}
                sx={harmonatePlainIconButtonSx}
              >
                <MoreVertIcon />
              </IconButton>
              
              {/* 3-dot menu dropdown */}
              <Menu 
                anchorEl={menuAnchorEl} 
                open={Boolean(menuAnchorEl)} 
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <Tooltip
                  title={!canViewWorkflow ? 'You do not have permission to view workflow details' : ''}
                >
                  <span>
                    <MenuItem onClick={handleReferenceFiles} disabled={!canViewWorkflow}>
                      Reference Files
                    </MenuItem>
                  </span>
                </Tooltip>
                <Tooltip
                  title={!canViewWorkflow ? 'You do not have permission to view workflow details' : ''}
                >
                  <span>
                    <MenuItem onClick={handleOutputView} disabled={!canViewWorkflow}>
                      Output
                    </MenuItem>
                  </span>
                </Tooltip>
              </Menu>
            </Box>
          </Box>
          
          <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, flex: 1, mb: 3, display: 'flex', flexDirection: 'column', minHeight: 480 }}>
            <DataGridPro
              rows={visibleWorkflowData}
              rowHeight={40}
              columns={[
                { 
                  field: 'documentType', 
                  headerName: 'Workflow Name', 
                  flex: 1,
                  resizable: true,
                  renderCell: (params) => (
                    <Box sx={{ fontWeight: params.row.id === 'unidentified' ? 'bold' : 'normal' }}>
                      {params.value}
                    </Box>
                  )
                },
                { 
                  field: 'inProcess', 
                  headerName: 'In Process', 
                  width: 120,
                  resizable: true,
                  align: 'center',
                  valueFormatter: (params) => params.value !== undefined ? params.value : '0'
                },
                { 
                  field: 'exceptions', 
                  headerName: 'Exceptions', 
                  width: 120,
                  resizable: true,
                  align: 'center',
                  valueFormatter: (params) => params.value !== undefined ? params.value : '0'
                },
                { 
                  field: 'complete', 
                  headerName: 'Completed', 
                  width: 120,
                  resizable: true,
                  align: 'center',
                  valueFormatter: (params) => params.value !== undefined ? params.value : '0'
                },
                { 
                  field: 'failed', 
                  headerName: 'Failed', 
                  width: 100,
                  resizable: true,
                  align: 'center',
                  valueFormatter: (params) => params.value !== undefined ? params.value : '0',
                  cellClassName: (params) => params.value > 0 ? 'error-cell' : ''
                },
              ]}
              onRowClick={handleRowClick}
              sx={{
                ...harmonateDataGridSx,
                border: 'none',
                '& .MuiDataGrid-row:hover': { bgcolor: ht.listBg, cursor: 'pointer' },
                '& .error-cell': { color: ht.red, fontWeight: 600 },
              }}
              disableSelectionOnClick
              pagination
              rowsPerPageOptions={[10, 25, 50]}
              pageSize={10}
              autoPageSize={false}
              noRowsOverlay={() => (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No workflows available
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={handleRetry}
                    sx={harmonateCancelButtonSx}
                  >
                    Refresh
                  </Button>
                </Box>
              )}
            />
          </Paper>
          
          <FileUploadDialog
            open={openUploadDialog}
            onClose={handleCloseUploadDialog}
            onUploadComplete={handleUploadComplete}
          />
        </>
      ) : currentView === 'workflowDetails' ? (
        isUnclassifiedView ? (
          <UnClassifiedWorkflowDrawer
            selectedDocumentType={selectedDocumentType}
            selectedWorkflowId={selectedWorkflowId}
            onFileClick={handleFileClick}
            selectedQuarter={selectedQuarter}
            onBackToWorkflows={handleBackToWorkflows}
            productScope={productScope}
          />
        ) : (
          <WorkflowDrawer
            selectedDocumentType={selectedDocumentType}
            selectedWorkflowId={selectedWorkflowId}
            onFileClick={handleFileClick}
            selectedQuarter={selectedQuarter}
            onBackToWorkflows={handleBackToWorkflows}
            productScope={productScope}
          />
        )
      ) : currentView === 'pdfViewer' ? (
        isViewingUnclassifiedFile ? (
          <UnClassifiedPDFViewer
            fileInstanceId={selectedFile?.fileInstanceId}
            fastApiFileInstanceId={selectedFastApiFileInstanceId}
            fileName={selectedFile?.fileName}
            fileDetails={{
              unitOfWorkId: selectedFile?.unitOfWorkId,
              approvedStatus: selectedFile?.approvedStatus
            }}
            onBack={() => setCurrentView('workflowDetails')}
            onBackToWorkflows={handleBackToWorkflows}
            productScope={productScope}
          />
        ) : (
          <PDFViewer
            fileInstanceId={selectedFile?.fileInstanceId}
            fastApiFileInstanceId={selectedFastApiFileInstanceId}
            fileName={selectedFile?.fileName}
            workflowInstanceId={selectedWorkflowInstanceId}
            workflowDefinitionSnowflakeId={selectedFile?.workflowDefinitionSnowflakeId}
            onBack={() => setCurrentView('workflowDetails')}
            onBackToWorkflows={handleBackToWorkflows}
            alerts={alerts}
            selectedDocumentType={selectedDocumentType}
            selectedWorkflowId={selectedWorkflowId}
            approvedStatus={selectedFile?.approvedStatus}
            productScope={productScope}
          />
        )
      ) : currentView === 'referenceDrawer' ? (
        <ReferenceDrawer
          onBackToWorkflows={handleBackToWorkflows}
          selectedQuarter={selectedQuarter}
          onNavigateToReferenceFiles={handleNavigateToReferenceFiles}
          productScope={productScope}
        />
      ) : currentView === 'referenceFiles' ? (
        <ReferenceFileList
          onBackToWorkflows={handleBackToWorkflows}
          onBackToReferenceDrawer={handleBackToReferenceDrawer}
          selectedQuarter={selectedQuarter}
          selectedReferenceData={selectedReferenceData}
          productScope={productScope}
        />
      ) : currentView === 'outputViewer' ? (
        <OutputViewer
          onBackToWorkflows={handleBackToWorkflows}
          productScope={productScope}
        />
      ) : null}

      {/* Common Toast Component */}
      <Toast 
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />
    </Box>
  );
};

export default Workflows;